import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserControlsContext } from '../contexts/UserControlsContext';
import { analyticsEnabled, analyticsIdentity } from '../lib/analytics';
import { routeSurface, type PageSurface } from '../lib/route-surface';

// Re-exported for back-compat: `routeSurface` now lives in `lib/route-surface`
// (a pure module the request interceptor can import without React deps).
export { routeSurface, type PageSurface };

/**
 * Fires a Segment `page` event on every route change (app-wide), stamped with
 * the identity spine + surface/is_flow_surface so funnels can isolate the Flow
 * loop. Mount ONCE, inside the router + UserControlsProvider (AuthenticatedLayout).
 *
 * Waits for identity (`userId`) before the first fire so the landing pageview
 * carries rep_id rather than firing anonymously. Dedups per path so a re-render
 * (or StrictMode's double-invoke) never double-counts the same page.
 */
export function usePageTracking(): void {
  const location = useLocation();
  const controls = useUserControlsContext();
  const lastFiredPath = useRef<string | null>(null);

  useEffect(() => {
    if (controls.userId == null) return; // wait until identity resolves
    if (!analyticsEnabled()) return;
    const path = location.pathname;
    if (lastFiredPath.current === path) return;
    lastFiredPath.current = path;

    const { page_name, surface, is_flow_surface } = routeSurface(path);
    try {
      // analytics-emit: Page Viewed
      window.analytics?.page(page_name, {
        ...analyticsIdentity(controls),
        page_name,
        path,
        surface,
        is_flow_surface,
      });
    } catch {}
  }, [location.pathname, controls.userId, controls]);
}
